/** Types mirroring the backend v1 API. */

export type Role = 'Admin' | 'SubAdmin' | 'User' | 'Collector' | 'SuperUserCollector' | 'Company' | 'SunilensSpecialAdmin';

export const ROLES: Role[] = ['Admin', 'SubAdmin', 'User', 'Collector', 'SuperUserCollector', 'Company', 'SunilensSpecialAdmin'];
export const STAFF_ROLES: Role[] = ['Admin', 'SubAdmin'];
export const COMPANY_ROLES: Role[] = ['Company', 'SunilensSpecialAdmin'];
export const COLLECTOR_ROLES: Role[] = ['Collector', 'SuperUserCollector'];

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface Profile {
  id: string;
  email: string;
  fullName: string;
  address: string | null;
  phoneNumber: string | null;
  latitude: number;
  longitude: number;
  role: Role;
  municipalityId: number | null;
  municipality: { id: number; name: string; isSpecial: boolean; regionCode: string | null } | null;
  companyId: number | null;
  companyName: string | null;
  allowChangeTrashQuality: boolean;
  allowDashboard: boolean;
  isActive: boolean;
  /** Temporarily locked after failed logins (UTC). */
  lockedUntil: string | null;
}

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  user: Profile;
}

export interface Region {
  id: number;
  name: string;
  regionCode: string;
  languageCode: string | null;
  dateFormat: string | null;
  timeFormat: string | null;
  currencyCode: string | null;
  currencySymbol: string | null;
}

export interface Municipality {
  id: number;
  name: string;
  isSpecial: boolean;
  isGlobal: boolean;
  showSmartBins: boolean;
  allowAwards: boolean;
  allowDashboard: boolean;
  regionId: number;
  region?: Region;
}

export interface Company {
  id: number;
  name: string;
  active: boolean;
  municipalityIds: number[];
}

export interface DocumentRef {
  id: string;
  name: string;
  contentType: string;
}

export interface RecycleType {
  id: number;
  name: string;
  color: string | null;
  labelHint: string | null;
  showForSelectionInApp: boolean;
  allowedForbidden: boolean;
  titleAllowed: string | null;
  allowed: string | null;
  titleForbidden: string | null;
  forbidden: string | null;
  titleRules: string | null;
  rules: string | null;
  imageId: number | null;
  image?: DocumentRef | null;
}

export interface RecycleTypeTranslation {
  id: number;
  recycleTypeId: number;
  regionId: number;
  region?: Region;
  name: string;
  titleAllowed: string | null;
  allowed: string | null;
  titleForbidden: string | null;
  forbidden: string | null;
  titleRules: string | null;
  rules: string | null;
  labelHint: string | null;
}

export interface Schedule {
  id: number;
  date: string;
  hourFrom: string;
  hourTo: string;
  municipalityId: number;
  municipality?: Municipality;
  locationDescription: string;
  latitude: number | null;
  longitude: number | null;
}

export enum AppointmentStatus {
  Init = 0,
  Collector = 1,
  Success = 2,
  Failed = 3,
  CollectorSpecial = 4,
}

export interface AppointmentView {
  id: number;
  code: string;
  codeKind: 'standard' | 'special';
  status: AppointmentStatus;
  points: number;
  collectorKg: number;
  companyKg: number;
  codeStatus: string | null;
  date: string;
  firstScannedAt: string | null;
  qualityUpdatedAt: string | null;
  user: { id: string; fullName?: string; address?: string | null; email?: string };
  schedule: { id: number; date?: string; hourFrom?: string; locationDescription?: string; municipalityId?: number; municipalityName?: string | null };
  recycleType: { id: number; name: string; color: string | null; labelHint: string | null } | null;
}

export interface BagView {
  id: number;
  date: string;
  status: number;
  user: { id: string; fullName?: string; address?: string | null; latitude?: number; longitude?: number; municipalityId?: number | null };
}

export interface PrizeBudget {
  id: number;
  dateFrom: string;
  dateTo: string;
  prizeType: number;
  numberOfPrizes: number;
  numberOfWinningsInPeriod: number;
  periodDays: number;
  numberOfWinnings: number;
}

export interface PrizeView {
  id: number;
  date: string;
  prizeType: number;
  wheelIndex?: number;
  pointsSpent: number;
  user: { id: string; fullName?: string; email?: string };
}

export interface PaymentView {
  id: number;
  date: string;
  points: number;
  amount: number;
  user: { id: string; fullName?: string; email?: string };
}

export interface RankRow {
  rank: number;
  userId: string;
  fullName: string;
  municipalityId: number;
  municipalityName: string;
  earned: number;
  spent: number;
  balance: number;
  bags: number;
}

/** One row of a user's points history (assembled from bags, spins and payments). */
export interface PointTransaction {
  id: string;
  amount: number;
  type: 'bag_confirmed' | 'prize_spin' | 'payment';
  note: string | null;
  createdAt: string;
}

export interface SmartBinView {
  id: number;
  title: string;
  locationDescription: string;
  latitude: number;
  longitude: number;
  progress: number;
  lastReportAt: string | null;
  municipalityId: number;
  municipalityName: string | null;
  recycleType: { id: number; name: string; color: string | null } | null;
  deviceKey?: string;
}

export interface SmartBinReport {
  id: number;
  previousProgress: number | null;
  newProgress: number | null;
  filledPercentageRaw: string | null;
  ipAddress: string | null;
  success: boolean;
  errorMessage: string | null;
  createdAt: string;
}

export interface DashboardSummary {
  scope: number[] | null;
  totals: {
    bags: number;
    openBags: number;
    withCollector: number;
    confirmed: number;
    contaminated: number;
    clean: number;
    totalKg: number;
    citizens: number;
    smartBins: number;
    fullSmartBins: number;
  };
  activity: { scannedToday: number; confirmedToday: number; confirmedLast7Days: number; confirmedThisMonth: number };
  byRecycleType: Array<{ id: number; name: string; color: string | null; bags: number; kg: number }>;
  byMonth: Array<{ month: string; bags: number; kg: number }>;
  topLocations: Array<{ location: string; bags: number; kg: number }>;
  recentActivity: Array<{ id: number; status: number; codeStatus: string | null; date: string; location: string; recycleType: string | null; fullName?: string }>;
}

export interface AuditEntry {
  id: number;
  action: string;
  entityType: string | null;
  entityId: string | null;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
  actorEmail: string | null;
}
